import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

import {
  type LiveClientPayload,
  type PublicTestClientPayload,
  useApi,
} from '#src/api/index.ts';

interface CredentialsContext {
  liveCredentials: LiveClientPayload[];
  publicTestCredentials: PublicTestClientPayload[];
  loading: boolean;
  refresh: () => Promise<void>;
  error: string | undefined;
}

const CredentialsContext = createContext<CredentialsContext>({
  liveCredentials: [],
  publicTestCredentials: [],
  loading: true,
  error: undefined,
  refresh: async () => {},
});

export const useCredentials = () => {
  const context = useContext(CredentialsContext);

  if (!context) {
    throw new Error('useCredentials must be used within a CredentialsProvider');
  }

  return context;
};

export const CredentialsProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi();

  const {
    data: clients,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['credentials', api],
    queryFn: ({ signal }) => api.credentials.list(signal),
  });

  const value = useMemo(
    () => ({
      liveCredentials: clients
        ? clients.filter(
            (client): client is LiveClientPayload => client.type === 'live',
          )
        : [],
      publicTestCredentials: clients
        ? clients.filter(
            (client): client is PublicTestClientPayload =>
              client.type === 'publicTest',
          )
        : [],
      error: error ? String(error) : undefined,
      refresh: async () => {
        await refetch();
      },
      loading: isLoading,
    }),
    [clients, isLoading, error, refetch],
  );

  return (
    <CredentialsContext.Provider value={value}>
      {children}
    </CredentialsContext.Provider>
  );
};
